import type { DirectCallInvite } from "../api/types/video";

/**
 * How long a direct-call invite rings before it is dead.
 *
 * Mirrors `RING_TIMEOUT_SECONDS = 45` in services/video (`meet/settings.py`),
 * which is env-overridable but server-only — there is no endpoint that hands
 * the number to the client, and a round-trip for a value nobody overrides is
 * not worth the request (GH-1600 decision Q-B).
 *
 * ⚠️ Drift risk: setting `RING_TIMEOUT_SECONDS` in the environment desyncs this
 * constant. If that ever happens, publish the value instead of raising it here.
 */
export const RING_TIMEOUT_MS = 45_000;

/**
 * An apparent age beyond this says the *clock* is wrong, not that the invite is
 * ancient: the server only ever reads invites inside the ring window
 * (`ringing.py`'s 45-second cutoff), so it cannot hand out an hour-old one.
 */
export const ABSURD_AGE_MS = 600_000;

/**
 * How long this invite has been ringing, in ms.
 *
 * Normally that is `now - created_at` — the server's clock is authoritative and
 * every client agrees on when the call was placed. But `created_at` is compared
 * against the *client's* clock, and a client whose clock runs fast would compute
 * a large age and hang up on a call that is genuinely ringing. So an age that is
 * unparseable, negative, or absurd (see `ABSURD_AGE_MS`) falls back to how long
 * *this tab* has known about the invite, which involves no cross-machine clock
 * comparison at all (GH-1600 decision Q-C).
 *
 * The fallback is deliberately generous: skew can only ever delay expiry to a
 * full window from first sight, never cut a live ring short.
 */
export function ringElapsedMs(
  createdAt: string,
  now: number,
  firstSeenAt: number,
): number {
  const created = Date.parse(createdAt);
  const age = now - created;
  if (Number.isNaN(created) || age < 0 || age > ABSURD_AGE_MS) {
    return Math.max(0, now - firstSeenAt);
  }
  return age;
}

/** Has this invite outlived the ring window? */
export function isRingExpired(
  createdAt: string,
  now: number,
  firstSeenAt: number,
): boolean {
  return ringElapsedMs(createdAt, now, firstSeenAt) >= RING_TIMEOUT_MS;
}

/** Milliseconds left before this invite expires; 0 once it has. */
export function ringRemainingMs(
  createdAt: string,
  now: number,
  firstSeenAt: number,
): number {
  return Math.max(0, RING_TIMEOUT_MS - ringElapsedMs(createdAt, now, firstSeenAt));
}

/** Convenience wrapper for call sites that hold the whole invite. */
export function isInviteExpired(
  invite: DirectCallInvite,
  now: number,
  firstSeenAt: number,
): boolean {
  return isRingExpired(invite.created_at, now, firstSeenAt);
}
